import { Menu, ChevronDown, ChevronUp, LogOut } from 'lucide-react';
import { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/utils/cn';
import { useAuthStore } from '@/hooks/useAuthStore';
import { useModuleAccess } from '@/hooks/usePermissions';
import NotificationBell from './NotificationBell';
import RibbonBand from './RibbonNav';
import { ribbonTabs, isPathActive } from './navConfig';

interface TopBarProps {
  onMenuClick: () => void;
  sidebarOpen?: boolean;
}

export default function TopBar({ onMenuClick }: TopBarProps) {
  const { user, logout } = useAuthStore();
  const canAccess = useModuleAccess();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const collapsedRef = useRef(false);
  const lockUntil = useRef(0);

  const handleLogout = async () => {
    setShowUserMenu(false);
    await logout();
    window.location.href = '/login';
  };

  // Single entry point for changing collapse state. Keeps the ref in sync and
  // briefly locks the scroll handler, so the layout shift caused by
  // collapsing/expanding the sticky header can't immediately re-trigger it.
  const applyCollapsed = (next: boolean) => {
    if (collapsedRef.current === next) return;
    collapsedRef.current = next;
    lockUntil.current = Date.now() + 400;
    setCollapsed(next);
  };

  // Auto-collapse the ribbon band on scroll down, expand on scroll up.
  useEffect(() => {
    let lastY = window.scrollY;
    let ticking = false;
    const update = () => {
      ticking = false;
      const y = window.scrollY;
      if (Date.now() < lockUntil.current) { lastY = y; return; } // ignore self-induced scroll
      const delta = y - lastY;
      if (Math.abs(delta) < 6) return;                            // ignore micro-scroll / momentum
      if (delta > 0 && y > 100) applyCollapsed(true);
      else if (delta < 0) applyCollapsed(false);
      lastY = y;
    };
    const onScroll = () => {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Filter tabs → groups → items by permission; drop empty groups/tabs.
  const visibleTabs = useMemo(
    () =>
      ribbonTabs
        .map((tab) => ({
          ...tab,
          groups: tab.groups
            .map((g) => ({ ...g, items: g.items.filter((i) => i.always || canAccess(i.module)) }))
            .filter((g) => g.items.length > 0),
        }))
        .filter((tab) => tab.groups.length > 0),
    [canAccess]
  );

  const tabForPath = (path: string) =>
    visibleTabs.find((t) => t.groups.some((g) => g.items.some((i) => isPathActive(path, i.href))))?.id;

  const [activeId, setActiveId] = useState<string | undefined>(
    () => tabForPath(location.pathname) || visibleTabs[0]?.id
  );

  // Keep the active tab in sync with the current route.
  useEffect(() => {
    const t = tabForPath(location.pathname);
    if (t) setActiveId(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, visibleTabs]);

  // Fall back to a valid tab if the current one disappears.
  useEffect(() => {
    if (!visibleTabs.some((t) => t.id === activeId)) setActiveId(visibleTabs[0]?.id);
  }, [visibleTabs, activeId]);

  const activeTab = visibleTabs.find((t) => t.id === activeId) || visibleTabs[0];

  return (
    <header className="sticky top-0 z-40 bg-card border-b border-border">
      {/* Row A: tab strip */}
      <div className="flex items-center h-12 px-3 gap-2">
        <button onClick={onMenuClick} className="lg:hidden p-2 hover:bg-accent rounded-md">
          <Menu className="h-6 w-6" />
        </button>

        <Link to="/dashboard" className="flex items-center gap-2 shrink-0">
          <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
            <span className="text-lg font-bold text-primary-foreground">C</span>
          </div>
          <span className="text-base font-semibold hidden sm:block">Cantier</span>
        </Link>

        <div className="hidden lg:block h-6 w-px bg-border mx-1" />

        {/* Ribbon tabs */}
        <div className="hidden lg:flex items-center gap-1">
          {visibleTabs.map((tab) => {
            const active = tab.id === activeId;
            return (
              <button
                key={tab.id}
                onClick={() => { setActiveId(tab.id); applyCollapsed(false); }}
                className={cn(
                  'px-4 h-9 rounded-md text-sm transition-colors',
                  active
                    ? 'bg-muted text-primary font-semibold'
                    : 'text-muted-foreground font-medium hover:bg-accent hover:text-foreground'
                )}
              >
                {tab.label}
              </button>
            );
          })}
          {/* Collapse / expand toggle */}
          <button
            onClick={() => applyCollapsed(!collapsed)}
            className="ml-1 p-1.5 rounded-md text-muted-foreground hover:bg-accent transition-colors"
            title={collapsed ? 'Expand ribbon' : 'Collapse ribbon'}
            aria-label={collapsed ? 'Expand ribbon' : 'Collapse ribbon'}
          >
            {collapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
          </button>
        </div>

        {/* Right cluster */}
        <div className="flex items-center gap-3 ml-auto shrink-0">
          <NotificationBell />
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-3 hover:bg-accent rounded-lg px-2 py-1.5"
            >
              <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center">
                <span className="text-sm font-semibold text-primary-foreground">
                  {user?.name.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="hidden md:block text-left">
                <p className="text-sm font-medium leading-tight">{user?.name}</p>
                <p className="text-xs text-muted-foreground leading-tight">{user?.role}</p>
              </div>
              <ChevronDown className="h-4 w-4 hidden md:block" />
            </button>

            {showUserMenu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowUserMenu(false)} />
                <div className="absolute top-full right-0 mt-2 w-56 bg-card border border-border rounded-lg shadow-lg z-20">
                  <div className="px-4 py-3 border-b border-border">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                  <Link
                    to="/settings"
                    onClick={() => setShowUserMenu(false)}
                    className="block w-full text-left px-4 py-2 text-sm hover:bg-accent"
                  >
                    Settings
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-destructive hover:bg-accent rounded-b-lg border-t border-border"
                  >
                    <LogOut className="h-4 w-4" />
                    <span>Logout</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Row B: ribbon band (desktop only, collapsible) */}
      <div
        className={cn(
          'hidden lg:block overflow-hidden bg-muted/30 transition-all duration-300 ease-in-out',
          collapsed ? 'max-h-0 opacity-0' : 'max-h-32 opacity-100 border-t border-border'
        )}
      >
        <div className="h-24">
          {activeTab && <RibbonBand groups={activeTab.groups} />}
        </div>
      </div>
    </header>
  );
}
